import ApiResponseV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/ApiResponseV2";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import HttpClientV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/HttpClientV2";
import { LazoFile } from "@/models/LazoFile";
import { LazoFileType } from "@/types/LazoFileType";

export const convertBase64ToFileWithDataType = (base64: string, dataType: string) => {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const file = new Blob([byteArray], { type: dataType });
  return file;
};

export const convertBase64ToFile = (base64: string, filename: string) => {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);

  const fileExtension = filename.split(".").pop();
  let fileType = "";
  switch (fileExtension) {
    case "pdf":
      fileType = "application/pdf";
      break;
    case "jpg":
      fileType = "image/jpg";
      break;
    case "jpeg":
      fileType = "image/jpeg";
      break;
    case "png":
      fileType = "image/png";
      break;
  }

  const file = new Blob([byteArray], { type: fileType });
  return file;
};

class LazoFileService {
  path: string;

  constructor(path: string) {
    this.path = path;
  }

  async uploadFile(file: File, fileType: LazoFileType): Promise<ApiResponseV2<LazoFile>> {
    const formData = new FormData();
    formData.append("file", file);

    return HttpClientV2.post(`${this.path}/upload/${fileType}`, formData, {
      axiosConfig: {
        timeout: 30000,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    });
  }

  async uploadFiles(files: File[], fileType: LazoFileType): Promise<ApiResponseV2<LazoFile[]>> {
    const formData = new FormData();
    files.forEach((f) => {
      formData.append("files", f);
    });
    return HttpClientV2.post(`${this.path}/upload-multiple-files/${fileType}`, formData, {
      axiosConfig: {
        timeout: 30000,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    });
  }

  async deleteFile(fileExternalId: string, fileType: LazoFileType) {
    return HttpClientV2.delete(`${this.path}/${fileExternalId}/${fileType}`);
  }

  getFiles(filesExternalIds: string[]): Promise<ApiResponseV2<LazoFile[]>> {
    return HttpClientV2.get(`${this.path}/multiple/${filesExternalIds.join(",")}`);
  }

  async downloadFile(fileExternalId: string, fileName: string): Promise<void> {
    return HttpClientV2.get<string>(`${this.path}/download/${fileExternalId}`).then((r) => {
      r.onSuccess((b) => {
        const file = convertBase64ToFile(b, fileName);
        const url = URL.createObjectURL(file);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        a.click();
      });
      r.onError((e) => {
        NotificationService.error("There was an error downloading the file. Error: " + e);
      });
    });
  }

  async downloadFileWithoutSave(files: string[]): Promise<Blob> {
    const response = await new Promise<string>((resolve, reject) => {
      HttpClientV2.get<string>(`${this.path}/download-multiple?fileExternalIds=${files.join(",")}`).then((r) => {
        r.onSuccess((b) => {
          resolve(b);
        });
        r.onError((e) => {
          NotificationService.error("Error downloading ");
          reject(e);
        });
      });
    });
    return convertBase64ToFile(response, "file");
  }

  async generatePreSignedUrl(fileExternalId: string): Promise<ApiResponseV2<string>> {
    return HttpClientV2.get(`${this.path}/generate-pre-signed-url/${fileExternalId}`);
  }
}

export default new LazoFileService("/api/file");
